import Phaser from "phaser";

/**
 * Kirby (finale Zoom-call reactions) and the Random PE Guy (Level 1 payoff NPC).
 * Both ship as packed 128px horizontal strips built by tools/pack-portrait.mjs
 * from the raw 256px grids. The texture key === the logical name below.
 */
const FRAME = 128;

/** Populated frame count per strip (mirrors pack-portrait.mjs output). */
export const PORTRAIT_FRAMES = {
  "kirby-idle": 16,
  "kirby-blink": 6,
  "kirby-talk": 32,
  "kirby-talk-start": 14,
  "kirby-laugh": 16,
  "kirby-cheer": 29,
  "kirby-clap": 46,
  "kirby-grimace": 31,
  "kirby-drink": 31,
  "kirby-oh": 27,
  "kirby-wave": 23,
  "kirby-cross-arms": 30,
  "pe-idle": 31,
  "pe-talk": 31,
  "pe-yell": 31,
  "pe-paper-show": 22,
  "pe-paper-hide": 10,
  "pe-money": 31,
  "pe-raise-eyebrow": 31,
} as const;

export type PortraitKey = keyof typeof PORTRAIT_FRAMES;

/** Per-anim overrides; anything unlisted loops at 12fps. yoyo smooths gesture
 *  sheets that end in a different pose than they start (so the loop doesn't snap);
 *  pe-money plays once and holds (the payoff "here's the offer" beat). */
type AnimCfg = { rate?: number; repeat?: number; yoyo?: boolean };
const CFG: Partial<Record<PortraitKey, AnimCfg>> = {
  "kirby-blink": { yoyo: true }, // sheet runs open->closed; yoyo returns to open for a natural blink
  "kirby-cheer": { yoyo: true },
  "kirby-clap": { rate: 16, yoyo: true },
  "kirby-grimace": { yoyo: true },
  "kirby-oh": { rate: 11, repeat: 0 }, // slower "oh"; play once and hold
  "kirby-cross-arms": { repeat: 0 }, // arms-down -> crossed; play once and hold the crossed pose
  "pe-paper-show": { yoyo: true },
  "pe-paper-hide": { yoyo: true },
  "pe-money": { repeat: 0 },
  "pe-raise-eyebrow": { repeat: 0 }, // play once and hold the last frame (the held eyebrow-raise beat)
};

const allKeys = () => Object.keys(PORTRAIT_FRAMES) as PortraitKey[];

/** Load the given portrait strips (all of them by default) as 128px spritesheets. */
export function loadPortraits(scene: Phaser.Scene, keys: PortraitKey[] = allKeys()) {
  for (const key of keys) {
    scene.load.spritesheet(key, `assets/sprites/${key}.png`, { frameWidth: FRAME, frameHeight: FRAME });
  }
}

/** Register one looping animation per portrait strip (idempotent). Anim key === texture key. */
export function registerPortraitAnims(scene: Phaser.Scene, keys: PortraitKey[] = allKeys()) {
  for (const key of keys) {
    if (scene.anims.exists(key)) continue;
    const cfg = CFG[key] ?? {};
    scene.anims.create({
      key,
      frames: scene.anims.generateFrameNumbers(key, { start: 0, end: PORTRAIT_FRAMES[key] - 1 }),
      frameRate: cfg.rate ?? 12,
      repeat: cfg.repeat ?? -1,
      yoyo: cfg.yoyo ?? false,
    });
  }
}
